import { create } from 'zustand';
import { SettlementRecord, SettlementPayment, RentDiscount } from '../types';
import { supabase } from '../api/supabase';

// ===== TYPE DEFINITIONS =====

export interface SettlementFilters {
  year?: number;
  month?: number;
  driverId?: string;
  vehicleId?: string;
  status?: SettlementRecord['status'] | SettlementRecord['status'][];
  assignmentId?: string;
}

export interface GenerationResult {
  created: number;
  skipped: number;
  errors: Array<{ assignment_id: string; error: string }>;
  settlements: Array<{
    assignment_id: string;
    driver_id: string;
    driver_name: string;
    vehicle_plate: string;
    total_days: number;
    net_balance: number;
    settlement_id: string;
  }>;
}

export interface PaymentData {
  paid_date: string;
  payment_reference: string;
  notes?: string;
}

// NEW: Enhanced payment data for partial payments
export interface NewPaymentData {
  payment_amount: number;
  payment_method: 'cash' | 'bank_transfer' | 'check' | 'offset' | 'other';
  payment_reference?: string;
  payment_date?: string; // Defaults to today if not provided
  notes?: string;
  recorded_by?: string; // User ID who recorded the payment
}

// NEW: Uber gap detection info
export interface UberGapInfo {
  is_complete: boolean;
  expected_days: number;
  actual_days: number;
  gap_days: number;
  gap_dates: string[];
}

export interface SettlementSummary {
  total_settlements: number;
  total_rent: number;
  total_income: number;
  total_expenses: number;
  net_balance: number;
  pending_count: number;
  approved_count: number;
  paid_count: number;
}

// ===== STORE INTERFACE =====

interface SettlementStore {
  settlements: SettlementRecord[];
  selectedSettlement: SettlementRecord | null;
  payments: SettlementPayment[];
  discounts: RentDiscount[];
  loading: boolean;
  error: string | null;
  
  // CRUD operations
  fetchSettlements: (filters?: SettlementFilters) => Promise<void>;
  fetchSettlementById: (id: string) => Promise<void>;
  generateMonthlySettlements: (year: number, month: number, autoApprove?: boolean) => Promise<GenerationResult>;
  generateSingleSettlement: (assignmentId: string, year: number, month: number) => Promise<string>;
  approveSettlement: (id: string, approvedBy: string) => Promise<void>;
  rejectSettlement: (id: string, reason: string) => Promise<void>;
  recordPayment: (settlementId: string, paymentData: NewPaymentData) => Promise<string>;
  getPaymentHistory: (settlementId: string) => Promise<SettlementPayment[]>;
  disputeSettlement: (id: string, reason: string) => Promise<void>;
  resolveDispute: (id: string) => Promise<void>;
  updateNotes: (id: string, notes: string) => Promise<void>;
  deleteSettlement: (id: string) => Promise<void>;
  
  // Helper functions
  getSettlementSummary: (year: number, month: number) => Promise<SettlementSummary>;
  checkUberGaps: (settlementId: string) => Promise<UberGapInfo | null>;
  setSelectedSettlement: (settlement: SettlementRecord | null) => void;
  clearError: () => void;
}

// ===== STORE IMPLEMENTATION =====

export const useSettlementStore = create<SettlementStore>((set, get) => ({
  settlements: [],
  selectedSettlement: null,
  payments: [],
  discounts: [],
  loading: false,
  error: null,

  fetchSettlements: async (filters?: SettlementFilters) => {
    set({ loading: true, error: null });
    try {
      let query = supabase
        .from('settlement_records')
        .select(`
          *,
          assignment:assignment_history(
            id,
            assignment_date,
            return_date,
            is_shared
          ),
          driver:drivers!driver_id(
            id,
            name,
            phone
          ),
          vehicle:vehicles!vehicle_id(
            id,
            license_plate,
            make_model
          )
        `)
        .order('period_year', { ascending: false })
        .order('period_month', { ascending: false })
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.year) {
        query = query.eq('period_year', filters.year);
      }
      if (filters?.month) {
        query = query.eq('period_month', filters.month);
      }
      if (filters?.driverId) {
        query = query.eq('driver_id', filters.driverId);
      }
      if (filters?.vehicleId) {
        query = query.eq('vehicle_id', filters.vehicleId);
      }
      if (filters?.assignmentId) {
        query = query.eq('assignment_id', filters.assignmentId);
      }
      if (filters?.status) {
        if (Array.isArray(filters.status)) {
          query = query.in('status', filters.status);
        } else {
          query = query.eq('status', filters.status);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ settlements: data || [], loading: false });
    } catch (error: any) {
      console.error('Error fetching settlements:', error);
      set({ error: error.message, loading: false });
    }
  },

  fetchSettlementById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('settlement_records')
        .select(`
          *,
          assignment:assignment_history(
            id,
            assignment_date,
            return_date,
            is_shared,
            assignment_type
          ),
          driver:drivers!driver_id(
            id,
            name,
            phone,
            license_number
          ),
          vehicle:vehicles!vehicle_id(
            id,
            license_plate,
            make_model,
            vehicle_code
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Fetch associated payments
      const { data: paymentsData } = await supabase
        .from('settlement_payments')
        .select('*')
        .eq('settlement_id', id)
        .order('payment_date', { ascending: false });

      // Fetch associated discounts
      const { data: discountsData } = await supabase
        .from('rent_discounts')
        .select(`
          *,
          driver:drivers!driver_id(name, phone),
          vehicle:vehicles!vehicle_id(license_plate, make_model)
        `)
        .eq('settlement_id', id)
        .order('discount_date', { ascending: false });

      set({ 
        selectedSettlement: data, 
        payments: paymentsData || [],
        discounts: discountsData || [],
        loading: false 
      });
    } catch (error: any) {
      console.error('Error fetching settlement:', error);
      set({ error: error.message, loading: false });
    }
  },

  generateMonthlySettlements: async (year: number, month: number, autoApprove = false) => {
    set({ loading: true, error: null });
    try {
      // Get all active assignments for the period
      const periodStart = new Date(year, month - 1, 1).toISOString();
      const periodEnd = new Date(year, month, 0).toISOString();

      const { data: assignments, error: assignmentError } = await supabase
        .from('assignment_history')
        .select('id, primary_driver_id, vehicle_id')
        .lte('assignment_date', periodEnd)
        .or(`return_date.is.null,return_date.gte.${periodStart}`);

      if (assignmentError) throw assignmentError;

      const results = {
        created: 0,
        skipped: 0,
        errors: [] as Array<{ assignment_id: string; error: string }>,
        settlements: [] as Array<{
          assignment_id: string;
          driver_id: string;
          driver_name: string;
          vehicle_plate: string;
          total_days: number;
          net_balance: number;
          settlement_id: string;
        }>,
      };

      // Generate settlement for each assignment using enhanced function
      for (const assignment of assignments || []) {
        try {
          const { data: settlementId, error } = await supabase.rpc(
            'generate_monthly_settlement_enhanced',
            {
              p_assignment_id: assignment.id,
              p_period_month: month,
              p_period_year: year,
            }
          );

          if (error) {
            results.errors.push({ assignment_id: assignment.id, error: error.message });
            results.skipped++;
          } else {
            // Fetch the created settlement for summary
            const { data: settlement } = await supabase
              .from('settlement_records')
              .select(`
                id,
                assignment_id,
                driver_id,
                total_days,
                net_balance,
                driver:drivers!driver_id!inner(name),
                vehicle:vehicles!vehicle_id!inner(license_plate)
              `)
              .eq('id', settlementId)
              .single();

            if (settlement) {
              const driver = Array.isArray(settlement.driver) ? settlement.driver[0] : settlement.driver;
              const vehicle = Array.isArray(settlement.vehicle) ? settlement.vehicle[0] : settlement.vehicle;

              results.settlements.push({
                assignment_id: settlement.assignment_id,
                driver_id: settlement.driver_id,
                driver_name: driver?.name || 'Unknown',
                vehicle_plate: vehicle?.license_plate || 'Unknown',
                total_days: settlement.total_days,
                net_balance: settlement.net_balance,
                settlement_id: settlement.id,
              });
              results.created++;

              // Auto-approve if requested
              if (autoApprove) {
                await supabase
                  .from('settlement_records')
                  .update({
                    status: 'approved',
                    approved_date: new Date().toISOString(),
                  })
                  .eq('id', settlementId);
              }
            }
          }
        } catch (err: any) {
          results.errors.push({ assignment_id: assignment.id, error: err.message });
          results.skipped++;
        }
      }

      set({ loading: false });

      // Refresh settlements list
      await get().fetchSettlements({ year, month });

      return results;
    } catch (error: any) {
      console.error('Error generating settlements:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  generateSingleSettlement: async (assignmentId: string, year: number, month: number) => {
    set({ loading: true, error: null });
    try {
      const { data: settlementId, error } = await supabase.rpc(
        'generate_monthly_settlement_enhanced',
        {
          p_assignment_id: assignmentId,
          p_period_month: month,
          p_period_year: year,
        }
      );

      if (error) throw error;

      set({ loading: false });

      // Refresh settlements
      await get().fetchSettlements({ year, month });

      return settlementId;
    } catch (error: any) {
      console.error('Error generating settlement:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  approveSettlement: async (id: string, approvedBy: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('settlement_records')
        .update({
          status: 'approved',
          approved_by: approvedBy,
          approved_date: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set((state) => ({
        settlements: state.settlements.map((s) =>
          s.id === id
            ? { ...s, status: 'approved', approved_by: approvedBy, approved_date: new Date().toISOString() }
            : s
        ),
        selectedSettlement:
          state.selectedSettlement?.id === id
            ? { ...state.selectedSettlement, status: 'approved', approved_by: approvedBy }
            : state.selectedSettlement,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Error approving settlement:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  rejectSettlement: async (id: string, reason: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('settlement_records')
        .update({
          status: 'cancelled',
          notes: reason,
        })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set((state) => ({
        settlements: state.settlements.map((s) =>
          s.id === id ? { ...s, status: 'cancelled', notes: reason } : s
        ),
        selectedSettlement:
          state.selectedSettlement?.id === id
            ? { ...state.selectedSettlement, status: 'cancelled', notes: reason }
            : state.selectedSettlement,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Error rejecting settlement:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  recordPayment: async (settlementId: string, paymentData: NewPaymentData) => {
    set({ loading: true, error: null });
    try {
      // Call the enhanced payment recording function
      const { data: paymentId, error } = await supabase.rpc('record_settlement_payment', {
        p_settlement_id: settlementId,
        p_payment_amount: paymentData.payment_amount,
        p_payment_method: paymentData.payment_method,
        p_payment_reference: paymentData.payment_reference || null,
        p_notes: paymentData.notes || null,
        p_recorded_by: paymentData.recorded_by || null,
      });

      if (error) throw error;

      // Refresh the settlement to get updated balance
      await get().fetchSettlementById(settlementId);

      set({ loading: false });

      return paymentId;
    } catch (error: any) {
      console.error('Error recording payment:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  getPaymentHistory: async (settlementId: string) => {
    try {
      const { data, error } = await supabase
        .from('settlement_payments')
        .select('*')
        .eq('settlement_id', settlementId)
        .order('payment_date', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error: any) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  },

  disputeSettlement: async (id: string, reason: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('settlement_records')
        .update({
          status: 'disputed',
          dispute_reason: reason,
        })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set((state) => ({
        settlements: state.settlements.map((s) =>
          s.id === id ? { ...s, status: 'disputed', dispute_reason: reason } : s
        ),
        selectedSettlement:
          state.selectedSettlement?.id === id
            ? { ...state.selectedSettlement, status: 'disputed', dispute_reason: reason }
            : state.selectedSettlement,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Error disputing settlement:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  resolveDispute: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('settlement_records')
        .update({
          status: 'approved',
          dispute_resolved_date: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set((state) => ({
        settlements: state.settlements.map((s) =>
          s.id === id
            ? { ...s, status: 'approved', dispute_resolved_date: new Date().toISOString() }
            : s
        ),
        selectedSettlement:
          state.selectedSettlement?.id === id
            ? { ...state.selectedSettlement, status: 'approved', dispute_resolved_date: new Date().toISOString() }
            : state.selectedSettlement,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Error resolving dispute:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateNotes: async (id: string, notes: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('settlement_records')
        .update({ notes })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set((state) => ({
        settlements: state.settlements.map((s) => (s.id === id ? { ...s, notes } : s)),
        selectedSettlement:
          state.selectedSettlement?.id === id ? { ...state.selectedSettlement, notes } : state.selectedSettlement,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Error updating notes:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteSettlement: async (id: string) => {
    set({ loading: true, error: null });
    try {
      // Get settlement data before deletion for activity log
      const { data: settlement } = await supabase
        .from('settlement_records')
        .select('*')
        .eq('id', id)
        .single();

      const { error } = await supabase.from('settlement_records').delete().eq('id', id);

      if (error) throw error;

      // Log settlement deletion
      if (settlement) {
        const { logActivity } = await import('../api/activityLog');
        await logActivity({
          entity_type: 'settlement',
          entity_id: id,
          action_type: 'delete',
          changes: {
            before: {
              driver_id: settlement.driver_id,
              year: settlement.year,
              month: settlement.month,
              total_amount: settlement.total_amount,
            },
          },
          description: `Deleted settlement for driver ${settlement.driver_id}`,
        });
      }

      // Update local state
      set((state) => ({
        settlements: state.settlements.filter((s) => s.id !== id),
        selectedSettlement: state.selectedSettlement?.id === id ? null : state.selectedSettlement,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Error deleting settlement:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  getSettlementSummary: async (year: number, month: number) => {
    try {
      // Call the helper function
      const { data, error } = await supabase.rpc('get_settlement_summary', {
        p_year: year,
        p_month: month,
      });

      if (error) throw error;

      return data[0] as SettlementSummary;
    } catch (error: any) {
      console.error('Error fetching settlement summary:', error);
      throw error;
    }
  },

  checkUberGaps: async (settlementId: string) => {
    try {
      const settlement = get().selectedSettlement;
      if (!settlement || settlement.id !== settlementId) {
        // Fetch if not loaded
        await get().fetchSettlementById(settlementId);
      }

      const currentSettlement = get().selectedSettlement;
      if (!currentSettlement) return null;

      // Extract Uber gap info from settlement
      if (currentSettlement.uber_data_complete === false) {
        return {
          is_complete: false,
          expected_days: currentSettlement.expected_uber_days || 0,
          actual_days: currentSettlement.actual_uber_days || 0,
          gap_days: currentSettlement.uber_gap_days || 0,
          gap_dates: currentSettlement.uber_gap_dates || [],
        };
      }

      return {
        is_complete: true,
        expected_days: currentSettlement.expected_uber_days || 0,
        actual_days: currentSettlement.actual_uber_days || 0,
        gap_days: 0,
        gap_dates: [],
      };
    } catch (error: any) {
      console.error('Error checking Uber gaps:', error);
      return null;
    }
  },

  setSelectedSettlement: (settlement: SettlementRecord | null) => {
    set({ selectedSettlement: settlement });
  },

  clearError: () => {
    set({ error: null });
  },
}));
